import React, { useState, useEffect } from "react";
import {
  IconButton,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as DeleteIcon } from "../../assets/dashboardIcons/deleteIcon.svg";
import { ReactComponent as AddIcon } from "../../assets/dashboardIcons/addIcon.svg";
import AziendaFrom from "./AziendaFrom";
import './rightSidebar.scss';
import { fetchCompanies, deleteCompany } from "../../features/company/companyThunk";
import { selectCompanies, selectIsLoading as selectCompanyLoading, selectError as selectCompanyError } from "../../features/company/companySlice";
import { fetchCompanyUsers, deleteCompanyUser } from "../../features/companyUser/companyUserThunk";
import { selectUsers, selectIsLoading as selectUserLoading, selectError as selectUserError } from "../../features/companyUser/companyUserSlice";
import UtentiForm from "./Utentiform";

const RightSidebar = () => {
  const [tabChange, setTabChange] = useState("Azienda");
  const [form, setForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const dispatch = useDispatch();
  
  // Get current user from auth state
  const currentUser = useSelector(state => state.auth.user);
  const isSuperAdmin = currentUser?.role_id === "SUPERADMIN";
  
  // Company selectors
  const allCompanies = useSelector(selectCompanies);
  const companyLoading = useSelector(selectCompanyLoading);
  const companyError = useSelector(selectCompanyError);
  
  // User selectors
  const allUsers = useSelector(selectUsers);
  const userLoading = useSelector(selectUserLoading);
  const userError = useSelector(selectUserError);
  
  // Filter companies based on user role
  const companies = isSuperAdmin ? allCompanies : allCompanies?.filter(company => company.id.toString() === currentUser?.company_id?.toString());
  // Filter users based on user role
  const users = allUsers;

  // Determine which loading and error states to use based on active tab
  const isLoading = tabChange === "Azienda" ? companyLoading : userLoading;
  const error = tabChange === "Azienda" ? companyError : userError;
  // Fetch data based on active tab
  useEffect(() => {
    if (tabChange === "Azienda") {
      dispatch(fetchCompanies());
    } 
    else if (tabChange === "Utenti") {
      // Pass company_id as a param for regular users to filter by their company
      const params = isSuperAdmin ? { search: searchTerm } : {
        search: searchTerm,
        company_id: currentUser?.company_id
      };
      dispatch(fetchCompanyUsers(params));
    }
  }, [dispatch, tabChange, isSuperAdmin, currentUser?.company_id]);
  // Fetch filtered users when search term changes for Users tab
  useEffect(() => {
    if (tabChange === "Utenti") {
      const timer = setTimeout(() => {
        const params = isSuperAdmin ? { search: searchTerm } : {
          search: searchTerm,
          company_id: currentUser?.company_id
        };
        dispatch(fetchCompanyUsers(params));
      }, 500); // Debounce search
      return () => clearTimeout(timer);
    }
  }, [dispatch, searchTerm, tabChange, isSuperAdmin, currentUser?.company_id]);
 
  const handleEditUser = (user) => {
    setSelectedUser(user); // open form with user data
    setForm(true);
  };
  const handleDeleteUser = (id) => {
    if (isSuperAdmin || currentUser?.id.toString() !== id.toString()) {
      dispatch(deleteCompanyUser(id));
    }
  };
  const handleEditCompany = (company) => {
    setSelectedCompany(company);
    setForm(true);
  };
  const handleDeleteCompany = (id) => {
    if (isSuperAdmin) {
      dispatch(deleteCompany(id));
    }
  };
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  const handleFormClose = () => {
    setForm(false);
    setSelectedUser(null);
    setSelectedCompany(null);
    if (tabChange === "Azienda") {
      dispatch(fetchCompanies());
    } else if (tabChange === "Utenti") {
      const params = isSuperAdmin ? {} : { company_id: currentUser?.company_id };
      dispatch(fetchCompanyUsers(params));
    }
  };

  const handleUserFormToggle = () => {
    if (form) {
      handleFormClose();
    } else {
      setSelectedUser(null); // very important for new user creation
      setForm(true);
    }
  };

  const handleTabChange = (tab) => {
    setTabChange(tab);
    setForm(false);
    setSearchTerm("");
  };
  // Filter companies based on search term
  const filteredCompanies = companies?.filter(company =>
    company.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  // Map role IDs to human-readable names
  const getRoleName = (roleId) => {
    const roleMap = {
      "ADMIN": "Amministratore",
      "COLLABORATOR": "Collaboratore",
      "OPERATOR": "Operatore",
      "SUPERADMIN": "Super Amministratore"
    };
    return roleMap[roleId] || roleId;
  };
  // Hide add buttons for non-superadmin users
  const canAddCompany = isSuperAdmin;
  const canAddUser = isSuperAdmin || currentUser?.role_id === "ADMIN";
  return (
    <>
      {tabChange === "Utenti" ? (
        <Box className='boxContainer'>
          <Box className='boxContainer__head'>
            <h2 variant="h6" className="boxContainer__title">Utenti</h2>
            {canAddUser && (
              <IconButton onClick={handleUserFormToggle}>
                {form ? "Indietro" : <AddIcon />}
              </IconButton>
            )}
          </Box>
          {form ? (
            <UtentiForm
              onClose={handleFormClose}
              selectedUser={selectedUser}
            />
          ) : (
            <>
              <TextField
                placeholder="Cerca un utente"
                className="customSearchBox"
                value={searchTerm}
                onChange={handleSearchChange}
              />
              {error && (
                <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
                  {error}
                </Alert>
              )}
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <ul className="boxContainer__lists Utentiform">
                  {users?.length > 0 ? (
                    users?.map((user) => (
                      <li key={user.id} className="boxContainer__list">
                        <div onClick={() => handleEditUser(user)}>
                          <p className="boxContainer__listName">{user.name}</p>
                          <p className="boxContainer__listEmail" variant="body2">{user.email}</p>
                        </div>
                        <div className="Utentiform__btnadmin">
                          <p variant="body2" className="boxContainer__listRole">{getRoleName(user.role_id)}</p>
                          {isSuperAdmin && (
                            <IconButton
                              className="deleteBtn"
                              edge="end"
                              onClick={() => handleDeleteUser(user.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </div>
                      </li>
                    ))
                  ) : (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      {searchTerm ? "Nessun utente trovato" : "Nessun utente disponibile"}
                    </Box>
                  )}
                </ul>
              )}
            </>
          )}
        </Box>
      ) : (
        <Box className='boxContainer'>
          <Box className='boxContainer__head'>
            <h2 className="boxContainer__title">{form ? "Registra una Cantina" : "Azienda"}</h2>
            {canAddCompany && (
              <IconButton onClick={() => {
                // For new company creation, clear the selection
                setSelectedCompany(null);
                setForm(!form);
              }}>
                {form ? "Indietro" : <AddIcon sx={{ color: "#1D4ED8" }} />}
              </IconButton>
            )}
          </Box>
          {form ? (
            <AziendaFrom onClose={handleFormClose} selectedCompany={selectedCompany} />
          ) : (
            <>
              <TextField
                className="customSearchBox"
                placeholder="Cerca un'Azienda"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
              />
              {error && (
                <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
                  {error}
                </Alert>
              )}
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <ul className="boxContainer__lists">
                  {filteredCompanies?.length > 0 ? (
                    filteredCompanies?.map((company) => (
                      <li key={company.id} className="boxContainer__list">
                        <div className='boxContainer__flexBox'>
                          {/* Clicking on the company name opens the edit form */}
                          <p className="boxContainer__flexBox-text" onClick={() => handleEditCompany(company)}>
                            {company.name}
                          </p>
                          {isSuperAdmin && (
                            <IconButton
                              edge="end"
                              className="deleteBtn"
                              onClick={() => handleDeleteCompany(company.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </div>
                      </li>
                    ))
                  ) : (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      {searchTerm ? "Nessuna azienda trovata" : "Nessuna azienda disponibile"}
                    </Box>
                  )}
                </ul>
              )}
            </>
          )}
        </Box>
      )}
      <Box className="tabbingBox">
        <Typography
          variant="body2"
          className={tabChange === "Azienda" ? "tabbingBox-text" : "tabbingBox-title"}
          onClick={() => handleTabChange("Azienda")}
        >
          Azienda
        </Typography>
        <Typography
          variant="body2"
          className={tabChange === "Utenti" ? "tabbingBox-text" : "tabbingBox-title"}
          onClick={() => handleTabChange("Utenti")}
        >
          Utenti
        </Typography>
      </Box>
    </>
  );
};
export default RightSidebar;